package com.ai.mitra.presentation.components

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.MoreVert
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.graphics.BlendMode
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.rotate
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalClipboardManager
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.ai.mitra.di.TextComposable
import com.ai.mitra.di.setClipData
import com.ai.mitra.di.toComposeImageBitmap
import com.ai.mitra.domain.model.ChatMessage
import com.ai.mitra.domain.model.Role
import com.ai.mitra.theme.borderColor
import com.ai.mitra.theme.lightBorderColor
import com.ai.mitra.theme.redColor
import com.ai.mitra.theme.textHintColor
import com.ai.mitra.theme.whiteColor
import mitra.composeapp.generated.resources.Res
import mitra.composeapp.generated.resources.ic_paste
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource


@Composable
fun MessageItem(chatMessage: ChatMessage, rotate: Float) {
    val coroutineScope = rememberCoroutineScope()
    val clipboardManager = LocalClipboardManager.current
    val isGEMINIMessage = chatMessage.participant != Role.YOU
    var expanded by remember { mutableStateOf(false) }

    val backgroundColor = when (chatMessage.participant) {
        Role.GEMINI -> borderColor
        Role.YOU -> borderColor
        Role.ERROR -> redColor
    }

    val horizontalAlignment = if (isGEMINIMessage) {
        Alignment.Start
    } else {
        Alignment.End
    }
    val circleColors = listOf(
        Color(0xFF5851D8),
        Color(0xFF833AB4),
        Color(0xFFC13584),
        Color(0xFFE1306C),
        Color(0xFFFD1D1D),
        Color(0xFFF56040),
        Color(0xFFF77737),
        Color(0xFFFCAF45),
        Color(0xFFFFDC80),
        Color(0xFF5851D8)
    )
    val cardShape = if (isGEMINIMessage) {
        RoundedCornerShape(
            16.dp, 16.dp, 16.dp, 0.dp
        )
    } else {
        RoundedCornerShape(
            16.dp, 16.dp, 0.dp, 16.dp
        )
    }

    val cardPadding = if (isGEMINIMessage) {
        PaddingValues(end = 24.dp)
    } else {
        PaddingValues(start = 24.dp)
    }

    Column(
        horizontalAlignment = horizontalAlignment,
        modifier = Modifier
            .padding(4.dp)
            .fillMaxWidth()
    ) {
        Box(
            modifier = Modifier.wrapContentSize().padding(cardPadding)
                .clip(cardShape)
                .padding(2.dp)
                .drawWithContent {
                    if (chatMessage.isPending) {
                        rotate(
                            rotate
                        ) {
                            drawCircle(
                                brush = Brush.sweepGradient(
                                    circleColors
                                ),
                                radius = size.width,
                                blendMode = BlendMode.SrcIn,
                            )
                        }
                    }
                    drawContent()
                }.background(backgroundColor, cardShape)
        ) {
            Column(
                modifier = Modifier.wrapContentSize()
            ) {
                if (chatMessage.images.isNotEmpty()) {
                    LazyRow(
                        modifier = Modifier.wrapContentSize().padding(bottom = 4.dp),
                        reverseLayout = true,
                        horizontalArrangement = Arrangement.spacedBy(4.dp, Alignment.End)
                    ) {
                        items(chatMessage.images) {
                            val bitmap = it.toComposeImageBitmap()
                            Image(
                                bitmap,
                                contentDescription = null,
                                modifier = Modifier.heightIn(max = 192.dp)
                                    .clip(RoundedCornerShape(16.dp)),
                                contentScale = ContentScale.FillHeight,
                            )
                        }
                    }
                }

                TextComposable(chatMessage.text, isGEMINIMessage)
                if (isGEMINIMessage) {
                    Row(
                        modifier = Modifier
                            .fillMaxWidth() // Fill the width of the parent.
                            .padding(16.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Box(
                            Modifier.weight(1f),
                        ) {
                            /*Text(
                                if (chatMessage.participant == Role.GEMINI) "by Gemini" else "by Error",
                                color = textHintColor,
                                fontSize = 12.sp,
                                modifier = Modifier.align(Alignment.CenterStart)
                                    .padding(start = 10.dp),
                                fontWeight = FontWeight.Light
                            )*/
                            IconButton(
                                modifier = Modifier.align(Alignment.CenterEnd),
                                colors = IconButtonDefaults.iconButtonColors(
                                    containerColor = lightBorderColor
                                ),
                                onClick = {
                                    coroutineScope.launch {
                                        setClipData(clipboardManager, chatMessage.text)
                                    }
                                }) {
                                Icon(
                                    painterResource(
                                        Res.drawable.ic_paste
                                    ),
                                    contentDescription = "copy",
                                    tint = whiteColor
                                )
                            }
                        }
                        Box(
                            modifier = Modifier
                                .wrapContentSize(Alignment.CenterEnd) // Aligns the box to the top-right corner.
                        ) {
                            val uriHandler = LocalUriHandler.current
                            IconButton(
                                colors = IconButtonDefaults.iconButtonColors(
                                    containerColor = lightBorderColor
                                ),
                                onClick = {
                                    expanded = true
                                }) {
                                Icon(
                                    imageVector = Icons.Filled.MoreVert,
                                    contentDescription = "Report offensive content",
                                    tint = whiteColor
                                )
                            }

                            DropdownMenu(
                                expanded = expanded,
                                onDismissRequest = { expanded = false }
                            ) {
                                DropdownMenuItem(
                                    onClick = {
                                        uriHandler.openUri("https://forms.gle/8i96i4gv9nVMGdwu9")
                                        expanded = false
                                    },
                                    text = { Text("Report offensive content") }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}



